import {
  Box,
  Button as ChakraButton,
  Center,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text as ChakraText,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Modal, showToast, Text } from '@diamond/shared/ui';
import { formatCurrencyIdr, unixToHumanDate } from '@diamond/shared/utils';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import type { ColDef, SelectionChangedEvent } from 'ag-grid-community';
import { useCallback, useMemo, useState } from 'react';

import {
  useAddLHCItemsMutation,
  useSearchLHCDeliveryOrdersMutation,
} from '../../hooks';
import type {
  SearchSuratJalanApiItem,
  SearchSuratJalanModalProps,
  SearchSuratJalanRowData,
} from '../../types';
import { CollectionSearchResultsSection } from '../collection-search-results-section';

const MIN_SEARCH_LENGTH = 8;

function mapSearchItem(item: SearchSuratJalanApiItem): SearchSuratJalanRowData {
  return {
    deliveryOrderId: item.delivery_order_id,
    deliveryOrderNo: item.delivery_order_number || '-',
    invoiceNo: item.invoice_no || '-',
    deliveryDate: item.delivery_date
      ? unixToHumanDate(item.delivery_date, 'd MMM yy')
      : '-',
    billTo: item.sap_no_bilto ? String(item.sap_no_bilto) : '-',
    customerName: item.customer_name || '-',
    outletName: item.outlet_name || '-',
    poNumber: item.po_number || '-',
    invoiceAmount: formatCurrencyIdr(item.invoice_amount),
  };
}

export function SearchSuratJalanModal({
  isOpen,
  onClose,
  lhcId,
  onAdded,
}: SearchSuratJalanModalProps) {
  const toast = useToast();
  const [searchValue, setSearchValue] = useState('');
  const [searchError, setSearchError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [rows, setRows] = useState<SearchSuratJalanRowData[]>([]);
  const [selectedRows, setSelectedRows] = useState<SearchSuratJalanRowData[]>(
    []
  );

  const searchMutation = useSearchLHCDeliveryOrdersMutation({
    onError: (error) => {
      showToast(toast, 'error', error.message || 'Gagal mencari surat jalan');
    },
  });
  const addItemsMutation = useAddLHCItemsMutation({
    onError: (error) => {
      showToast(
        toast,
        'error',
        error.message || 'Gagal menambahkan surat jalan'
      );
    },
  });

  const resetAndClose = useCallback(() => {
    setSearchValue('');
    setSearchError('');
    setHasSearched(false);
    setRows([]);
    setSelectedRows([]);
    onClose();
  }, [onClose]);

  const handleSearch = useCallback(async () => {
    const trimmedSearch = searchValue.trim();
    if (trimmedSearch.length < MIN_SEARCH_LENGTH) {
      setSearchError('Nomor minimal 8 karakter untuk pencarian.');
      return;
    }

    setSearchError('');
    const result = await searchMutation.mutateAsync({
      lhcId,
      searchQuery: trimmedSearch,
    });
    setHasSearched(true);
    setRows(result.data.map(mapSearchItem));
    setSelectedRows([]);
  }, [lhcId, searchMutation, searchValue]);

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent<SearchSuratJalanRowData>) => {
      setSelectedRows(event.api.getSelectedRows());
    },
    []
  );

  const handleAdd = useCallback(async () => {
    if (!selectedRows.length) return;

    await addItemsMutation.mutateAsync({
      lhcId,
      deliveryOrderIds: selectedRows.map((row) => row.deliveryOrderId),
    });

    showToast(toast, 'success', 'Surat jalan berhasil ditambahkan');
    onAdded();
    resetAndClose();
  }, [addItemsMutation, lhcId, onAdded, resetAndClose, selectedRows, toast]);

  const columnDefs = useMemo<ColDef<SearchSuratJalanRowData>[]>(
    () => [
      {
        colId: 'selection',
        headerName: '',
        width: 42,
        minWidth: 42,
        maxWidth: 42,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        pinned: 'left',
        sortable: false,
        resizable: false,
        cellStyle: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
      },
      {
        field: 'deliveryOrderNo',
        headerName: 'Nomor SJ',
        width: 140,
        tooltipField: 'deliveryOrderNo',
      },
      {
        field: 'invoiceNo',
        headerName: 'No. Faktur',
        width: 140,
        tooltipField: 'invoiceNo',
      },
      { field: 'deliveryDate', headerName: 'Tgl. SJ', width: 75 },
      { field: 'billTo', headerName: 'No. Bill to', width: 90 },
      {
        field: 'customerName',
        headerName: 'Nama Customer',
        width: 140,
        tooltipField: 'customerName',
      },
      {
        field: 'outletName',
        headerName: 'Nama Outlet',
        width: 100,
        tooltipField: 'outletName',
      },
      {
        field: 'poNumber',
        headerName: 'PO SOL',
        width: 120,
        tooltipField: 'poNumber',
      },
      {
        field: 'invoiceAmount',
        headerName: 'Nilai Faktur',
        minWidth: 100,
        flex: 1,
      },
    ],
    []
  );

  const rowHeight = 42;
  const headerHeight = 48;
  const maxTableHeight = headerHeight + 5 * rowHeight;

  const dynamicHeight = rows.length
    ? `${Math.min(
        headerHeight + rows.length * rowHeight + 2,
        maxTableHeight
      )}px`
    : '46px';

  const tableHeight = dynamicHeight;
  const showEmptySearchState =
    hasSearched && rows.length === 0 && !searchMutation.isPending;

  return (
    <Modal
      title="Cari No. Surat Jalan / Faktur"
      name="search-surat-jalan-modal"
      isOpen={isOpen}
      onClose={resetAndClose}
      size="5xl"
      isCentered
      hideAction
      scrollBehavior="outside"
      modalBodyProps={{ pb: 6 }}
      modalHeaderProps={{
        fontWeight: 500,
        fontSize: '24px',
        lineHeight: '100%',
        letterSpacing: '0%',
        color: '#373F41',
      }}
    >
      <FormControl mb={8}>
        <FormLabel
          fontWeight={500}
          fontSize="10px"
          lineHeight="100%"
          letterSpacing="0%"
          color="#373F41"
          mb={2}
        >
          Nomor Surat Jalan / Faktur
        </FormLabel>
        <Box display="flex" gap={3} alignItems="flex-start">
          <InputGroup maxW="320px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon fontSize="small" style={{ color: '#A0AEC0' }} />
            </InputLeftElement>
            <Input
              value={searchValue}
              placeholder="Cari no. surat jalan / faktur"
              onChange={(event) => setSearchValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  void handleSearch();
                }
              }}
              borderColor="#0000A3"
            />
            {searchValue && (
              <InputRightElement>
                <IconButton
                  aria-label="Bersihkan pencarian"
                  icon={<CloseIcon style={{ fontSize: 14 }} />}
                  size="xs"
                  variant="ghost"
                  onClick={() => {
                    setSearchValue('');
                    setSearchError('');
                  }}
                />
              </InputRightElement>
            )}
          </InputGroup>
          <ChakraButton
            bgColor="#0000A3"
            color="white"
            _hover={{ bgColor: '#00008A' }}
            onClick={() => {
              void handleSearch();
            }}
            isLoading={searchMutation.isPending}
          >
            Cari
          </ChakraButton>
        </Box>
        {searchError ? (
          <ChakraText mt={2} fontSize="xs" color="red.500">
            {searchError}
          </ChakraText>
        ) : null}
      </FormControl>

      <CollectionSearchResultsSection<SearchSuratJalanRowData>
        columnDefs={columnDefs}
        rowData={rows}
        isLoading={searchMutation.isPending || addItemsMutation.isPending}
        height={tableHeight}
        minHeight={tableHeight}
        gridOptions={{
          rowSelection: 'multiple',
          onSelectionChanged: handleSelectionChanged,
          suppressNoRowsOverlay: true,
          getRowId: (params) => params.data?.deliveryOrderId ?? '',
          defaultColDef: {
            sortable: false,
            resizable: false,
          },
        }}
        sectionSx={{
          '& .ag-theme-diamond .ag-header-cell-text': {
            fontWeight: '500 !important',
            fontSize: '10px !important',
            color: '#373F41 !important',
          },
          '& .ag-theme-diamond .ag-selection-checkbox': {
            margin: '0 !important',
          },
        }}
        emptyState={
          showEmptySearchState ? (
            <Center py={12}>
              <VStack spacing={4}>
                <Text
                  fontWeight={500}
                  fontSize="20px"
                  lineHeight="100%"
                  letterSpacing="0%"
                  textAlign="center"
                  color="#000000"
                >
                  Nomor Surat Jalan tidak ditemukan
                </Text>
                <Text
                  fontWeight={400}
                  fontSize="16px"
                  lineHeight="24px"
                  letterSpacing="0%"
                  textAlign="center"
                  color="#8E8E93"
                >
                  Silakan cari nomor surat jalan untuk membuat data Laporan
                  Harian Anda
                </Text>
                <ChakraButton
                  mt={2}
                  leftIcon={<AutorenewIcon sx={{ fontSize: 16 }} />}
                  bgColor="#0000A3"
                  color="white"
                  _hover={{ bgColor: '#00008A' }}
                  onClick={() => {
                    void handleSearch();
                  }}
                  isLoading={searchMutation.isPending}
                >
                  Sinkronisasi Ulang
                </ChakraButton>
              </VStack>
            </Center>
          ) : null
        }
      />

      <Box display="flex" justifyContent="flex-end" mt={6}>
        <ChakraButton
          bgColor="#0000A3"
          color="white"
          _hover={{ bgColor: '#00008A' }}
          onClick={() => {
            void handleAdd();
          }}
          isDisabled={!selectedRows.length || addItemsMutation.isPending}
          isLoading={addItemsMutation.isPending}
        >
          Tambahkan
        </ChakraButton>
      </Box>
    </Modal>
  );
}
